import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft } from 'lucide-react';
import { useAiSettings } from '@/features/ai-settings/api';
import { generateFormSchema, type GenerateFormValues } from '@/features/recipes/generateFormSchema';
import { useGenerateRecipe } from '@/features/recipes/generateMutation';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ApiClientError } from '@/lib/api';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/recipes/generate')({
  component: GenerateRecipe,
});

function GenerateRecipe() {
  const navigate = useNavigate();
  const { data: aiSettings } = useAiSettings();
  const generate = useGenerateRecipe();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GenerateFormValues>({
    resolver: zodResolver(generateFormSchema),
    defaultValues: { text: '', model: '' },
  });

  const onSubmit = async (values: GenerateFormValues) => {
    setSubmitError(null);
    try {
      const recipe = await generate.mutateAsync({
        text: values.text,
        model: values.model?.trim() || undefined,
      });
      await navigate({ to: '/recipes/$id/edit', params: { id: recipe.id } });
    } catch (e) {
      setSubmitError(
        e instanceof ApiClientError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Generation failed',
      );
    }
  };

  const defaultModel = aiSettings?.model ?? '';
  const isPending = generate.isPending;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <Link
          to="/recipes"
          className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-3"
        >
          <ChevronLeft size={16} />
          Recipes
        </Link>
        <h1 className="font-serif text-3xl leading-tight">Generate with AI</h1>
        <p className="text-ink-soft text-sm mt-1">
          Describe a recipe and the active system prompt will be combined with your text.
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <Textarea
          label="Prompt"
          rows={10}
          placeholder="e.g. A warming Thai green curry with tofu, jasmine rice, and plenty of fresh herbs."
          hint="This text is combined with the active system prompt. The more detail you provide, the better the result."
          error={errors.text?.message}
          disabled={isPending}
          {...register('text')}
        />

        <Input
          label="Model"
          placeholder={defaultModel || 'Leave blank to use the configured default'}
          hint={
            defaultModel
              ? `Leave blank to use the configured default: ${defaultModel}`
              : 'Leave blank to use the configured default.'
          }
          error={errors.model?.message}
          disabled={isPending}
          {...register('model')}
        />

        {submitError && (
          <div
            className="text-xs rounded-lg p-3"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
          >
            {submitError}
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          {isPending && (
            <p className="text-sm text-ink-soft">Generating recipe… this can take 30–60 s.</p>
          )}
          {!isPending && <div />}
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Generating…' : 'Generate recipe'}
          </Button>
        </div>
      </form>
    </div>
  );
}
